import { useCallback } from 'react';
import { useLatest } from '@/shared/hooks/useLatest';
import {
  useLegacyMessaging,
  LegacySelectionStyle,
  readSelectedIds,
} from '@/shared/lib/legacyMagic';
import type { ListPickerMode } from '../../types';
import type { AsyncSelectorMode } from '../AsyncSelector.types';

export interface UseAsyncSelectorLegacyNavOptions<TItem> {
  objectCategory?: string;
  form?: string;
  listPicker: ListPickerMode;
  mode: AsyncSelectorMode;
  selected: readonly TItem[];
  getKey: (item: TItem) => string | number;
  onChange: (next: readonly TItem[]) => void;
  resolveByIds?: (ids: readonly (string | number)[]) => Promise<readonly TItem[]>;
  openPickModal: () => void;
  resetSuggestions: () => void;
}

export function useAsyncSelectorLegacyNav<TItem>({
  objectCategory,
  form,
  listPicker,
  mode,
  selected,
  getKey,
  onChange,
  resolveByIds,
  openPickModal,
  resetSuggestions,
}: UseAsyncSelectorLegacyNavOptions<TItem>) {
  const hasNavigation = (objectCategory ?? form) != null;
  const { send, isPending } = useLegacyMessaging();

  const selectedRef = useLatest(selected);
  const getKeyRef = useLatest(getKey);
  const onChangeRef = useLatest(onChange);
  const resolveByIdsRef = useLatest(resolveByIds);
  const modeRef = useLatest(mode);

  const buildNavPayload = useCallback(
    (extra: Record<string, unknown>): Record<string, unknown> => ({
      ...(objectCategory != null ? { ObjectCategory: objectCategory } : {}),
      ...(form != null ? { Form: form } : {}),
      ...extra,
    }),
    [objectCategory, form]
  );

  const handleCreate = useCallback(() => {
    if (!hasNavigation) return;
    send('form/new', buildNavPayload({}), (msg) => {
      if (!msg.data?.success || msg.data.id == null || !resolveByIdsRef.current) return;
      const id = msg.data.id;
      void resolveByIdsRef
        .current([id])
        .then((resolved) => {
          const item = resolved[0];
          if (!item) return;
          if (modeRef.current === 'single') {
            onChangeRef.current([item]);
            return;
          }
          onChangeRef.current([...selectedRef.current, item]);
        })
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs stable
  }, [send, hasNavigation, buildNavPayload]);

  const handleEditItem = useCallback(
    (item: TItem) => {
      if (!hasNavigation) return;
      const id = getKey(item);
      send('form/edit', buildNavPayload({ ID: id }), (msg) => {
        if (!msg.data?.success || msg.data.id == null || !resolveByIdsRef.current) return;
        const rid = msg.data.id;
        void resolveByIdsRef
          .current([rid])
          .then((resolved) => {
            const resolvedItem = resolved[0];
            if (!resolvedItem) return;
            const rk = getKeyRef.current(resolvedItem);
            const current = selectedRef.current;
            if (modeRef.current === 'single') {
              onChangeRef.current([resolvedItem]);
              return;
            }
            const exists = current.some((e) => getKeyRef.current(e) === rk);
            onChangeRef.current(
              exists
                ? current.map((e) => (getKeyRef.current(e) === rk ? resolvedItem : e))
                : [...current, resolvedItem]
            );
          })
          .catch(() => undefined);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs stable
    [send, hasNavigation, buildNavPayload, getKey]
  );

  const handleOpenList = useCallback(() => {
    if (!hasNavigation) return;
    if (listPicker === 'legacy') {
      const selectionStyle =
        modeRef.current === 'multi' ? LegacySelectionStyle.Multi : LegacySelectionStyle.Single;
      send('form/list', buildNavPayload({ SelectionStyle: selectionStyle }), (msg) => {
        if (!msg.data?.success || !resolveByIdsRef.current) return;
        const ids = readSelectedIds(msg.data);
        void (ids.length === 0 ? Promise.resolve([]) : resolveByIdsRef.current(ids))
          .then((items) => {
            onChangeRef.current(modeRef.current === 'single' ? items.slice(0, 1) : items);
          })
          .catch(() => undefined);
      });
      return;
    }
    resetSuggestions();
    openPickModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs stable
  }, [send, hasNavigation, buildNavPayload, listPicker, resetSuggestions, openPickModal]);

  return {
    hasNavigation,
    isPending,
    handleCreate,
    handleEditItem,
    handleOpenList,
  };
}
